// src/invoices/priceLists.js — екранът „Ценови листи" (т. 5 a, 17.10.2026).
//
// Артикули → „Ценови листи": листи като „Едро", „Дистрибутори". Всяка има
// общ процент отстъпка от основната цена и/или своя цена на артикул (по-
// силна от процента). Листата се дава на КЛИЕНТА (картата му) — във
// фактурата и проформата цената идва сама. Внос/износ на цените в Excel.

import { el, clear, field, input, row, clickableRow } from '../shared/dom.js';
import { toast } from '../shared/toast.js';
import { api } from '../shared/api.js';
import { loadError, searchInput } from '../shared/ui.js';
import { openModal, closeModal, confirmDialog } from '../shared/modal.js';
import { state, cq } from '../shared/state.js';
import { dateInput, isoToBg } from '../shared/dates.js';
import { amount } from '../shared/format.js';
import { xlsxDownload } from '../shared/xlsx.js';
import { impReadFile, impPick, impNum } from '../shared/importers.js';
import priceLists from '../../services/priceLists.js';

function validity(l) {
  if (l.valid_from && l.valid_to) return isoToBg(l.valid_from) + ' – ' + isoToBg(l.valid_to);
  if (l.valid_from) return 'от ' + isoToBg(l.valid_from);
  if (l.valid_to) return 'до ' + isoToBg(l.valid_to);
  return 'винаги';
}

export async function priceListsDialog(after) {
  const box = el('div', {});
  async function draw() {
    clear(box);
    let lists = [];
    let contacts = [];
    try {
      [lists, contacts] = await Promise.all([api('/price-lists' + cq()), api('/contacts' + cq()).catch(() => [])]);
      if (!Array.isArray(lists)) lists = [];
    } catch (e) { box.appendChild(loadError(draw, 'Ценовите листи не можаха да се заредят.')); return; }
    box.appendChild(el('button', { class: 'btn sm', type: 'button', text: '+ Нова листа', style: 'margin-bottom:10px', onClick: () => listForm(null, draw) }));
    if (!lists.length) {
      box.appendChild(el('p', { style: 'color:var(--muted)', text: 'Още няма ценови листи. Направете листа (напр. „Едро"), сложете процент или цени по артикул и я изберете в картата на клиента — цената му ще идва сама във фактурата.' }));
      return;
    }
    const tb = el('tbody');
    lists.slice().sort((x, y) => String(x.name || '').localeCompare(String(y.name || ''), 'bg')).forEach((l) => {
      const n = (Array.isArray(contacts) ? contacts : []).filter((c) => Number(c.price_list_id) === Number(l.id)).length;
      const tr = clickableRow(() => listForm(l, draw), 'Отвори листа ' + (l.name || ''),
        el('td', { style: 'font-weight:600' }, l.name || ''),
        el('td', { class: 'num' }, Number(l.discount_pct) ? '−' + amount(l.discount_pct).replace(/,00$/, '') + ' %' : '—'),
        el('td', { class: 'num' }, String((l.prices || []).length)),
        el('td', {}, validity(l)),
        el('td', { class: 'num' }, String(n)));
      if (l.active === false) tr.style.opacity = '0.55';
      tb.appendChild(tr);
    });
    box.appendChild(el('table', { class: 'list' },
      el('thead', {}, el('tr', {}, el('th', { text: 'Листа' }), el('th', { class: 'num', text: 'Отстъпка' }), el('th', { class: 'num', text: 'Цени по артикул' }), el('th', { text: 'Важи' }), el('th', { class: 'num', text: 'Клиенти' }))),
      tb));
  }
  const foot = el('div', {}, el('button', { class: 'btn', type: 'button', text: 'Готово', onClick: () => { closeModal(); if (after) after(); } }));
  openModal({ title: 'Ценови листи', body: box, foot, wide: true });
  await draw();
}

async function listForm(existing, after) {
  const l = existing || {};
  let arts = [];
  try { arts = await api('/articles' + cq()); if (!Array.isArray(arts)) arts = []; } catch (e) { toast(e.message, true); return; }
  arts = arts.filter((a) => a.active !== false).sort((x, y) => String(x.name || '').localeCompare(String(y.name || ''), 'bg'));
  const own = new Map((l.prices || []).map((p) => [Number(p.article_id), p.price]));
  const f = {
    name: input({ value: l.name || '', maxlength: '120', placeholder: 'напр. Едро' }),
    pct: input({ type: 'number', step: '0.01', min: '0', max: '99', value: Number(l.discount_pct) ? l.discount_pct : '', placeholder: '0' }),
    from: dateInput(l.valid_from || ''),
    to: dateInput(l.valid_to || ''),
    active: el('input', { type: 'checkbox', checked: l.active !== false }),
  };
  // Редовете с артикулите: празна цена = по процента (подсказката го казва).
  const priceInputs = new Map();
  const tb = el('tbody');
  const hintOf = (a) => {
    const pct = Number(String(f.pct.value).replace(',', '.')) || 0;
    const base = Number(a.unit_price) || 0;
    return pct > 0 && pct < 100 ? amount(base * (1 - pct / 100)) : amount(base);
  };
  arts.forEach((a) => {
    const inp = input({ type: 'number', step: '0.01', min: '0', value: own.has(Number(a.id)) ? own.get(Number(a.id)) : '', 'aria-label': 'Цена в листата, ' + (a.name || ''), style: 'max-width:110px' });
    inp.placeholder = hintOf(a);
    priceInputs.set(Number(a.id), inp);
    const tr = el('tr', {}, el('td', {}, a.code || ''), el('td', {}, a.name || ''), el('td', { class: 'num' }, amount(Number(a.unit_price) || 0)), el('td', { class: 'num' }, inp));
    tr.dataset.q = ((a.code || '') + ' ' + (a.name || '')).toLowerCase();
    tb.appendChild(tr);
  });
  f.pct.addEventListener('input', () => arts.forEach((a) => { priceInputs.get(Number(a.id)).placeholder = hintOf(a); }));
  const search = searchInput({ placeholder: 'Търсене на артикул…', onSearch: (q) => { [...tb.children].forEach((tr) => { tr.style.display = !q || tr.dataset.q.includes(q) ? '' : 'none'; }); } });
  const table = arts.length
    ? el('div', { style: 'max-height:45vh;overflow:auto;margin-top:6px' }, el('table', { class: 'list' },
      el('thead', {}, el('tr', {}, el('th', { text: 'Код' }), el('th', { text: 'Артикул' }), el('th', { class: 'num', text: 'Основна цена' }), el('th', { class: 'num', text: 'Цена в листата' }))), tb))
    : el('p', { style: 'color:var(--muted)', text: 'В каталога още няма артикули — листата може да е само с процент.' });

  const exportXlsx = () => {
    const data = arts.map((a) => ({ 'Код': a.code || '', 'Артикул': a.name || '', 'Основна цена': Number(a.unit_price) || 0, 'Цена в листата': priceInputs.get(Number(a.id)).value === '' ? '' : Number(priceInputs.get(Number(a.id)).value) }));
    xlsxDownload(data, 'Цени', 'cenova-lista-' + (f.name.value.trim() || 'nova') + '.xlsx');
  };
  // Вносът попълва полетата (не записва): човекът вижда и натиска „Запази".
  const importXlsx = () => impReadFile((rows) => {
    const byCode = new Map(arts.filter((a) => a.code).map((a) => [String(a.code).trim(), a]));
    const byName = new Map(arts.map((a) => [String(a.name || '').trim().toLowerCase(), a]));
    let ok = 0; const miss = [];
    rows.forEach((r) => {
      const code = impPick(r, ['Код', 'код', 'Code']);
      const name = impPick(r, ['Артикул', 'Наименование', 'Име']);
      const raw = impPick(r, ['Цена в листата', 'Цена', 'Цена по листа']);
      if (raw === '') return;
      const a = (code && byCode.get(code)) || byName.get(String(name).toLowerCase());
      if (!a) { miss.push(code || name); return; }
      priceInputs.get(Number(a.id)).value = String(impNum(raw));
      ok++;
    });
    toast('Попълнени ' + ok + ' цени' + (miss.length ? '; без артикул в каталога: ' + miss.slice(0, 5).join(', ') + (miss.length > 5 ? '…' : '') : '') + '. Натиснете „Запази".', !!miss.length && !ok);
  });

  const body = el('div', {},
    row(field('Име на листата', f.name), field('Отстъпка от основната цена, %', f.pct, { size: 's' })),
    row(field('Важи от (по избор)', f.from, { size: 'm' }), field('Важи до (по избор)', f.to, { size: 'm' }),
      el('div', { class: 'field' }, el('label', { class: 'choice' }, f.active, el('span', { text: 'Активна' })))),
    el('p', { style: 'margin:2px 0 8px;font-size:var(--fs-sm);color:var(--muted)', text: 'Цена на артикул е по-силна от процента. Празна клетка = по процента (в сиво), а без процент — основната цена. Листата се дава на клиента в картата му.' }),
    arts.length ? el('div', { style: 'display:flex;gap:8px;align-items:center;flex-wrap:wrap' }, search,
      el('button', { class: 'btn sm ghost', type: 'button', text: 'Изнеси в Excel', onClick: exportXlsx }),
      el('button', { class: 'btn sm ghost', type: 'button', text: 'Внеси от Excel', onClick: importXlsx })) : null,
    table);

  const save = async () => {
    const p = {
      company_id: state().activeCompany,
      name: f.name.value.trim(),
      discount_pct: f.pct.value,
      valid_from: f.from.dataset.iso || '',
      valid_to: f.to.dataset.iso || '',
      active: f.active.checked,
      prices: arts.map((a) => ({ article_id: a.id, price: priceInputs.get(Number(a.id)).value })).filter((x) => x.price !== ''),
    };
    const chk = priceLists.shape(p, null, null);
    if (chk.error) { toast(chk.error, true); return; }
    try {
      if (existing && existing.id) await api('/price-lists/' + existing.id, { method: 'PUT', body: JSON.stringify(p) });
      else await api('/price-lists', { method: 'POST', body: JSON.stringify(p) });
      toast('Листата „' + p.name + '" е записана.');
      closeModal();
      after();
    } catch (e) { toast(e.message, true); }
  };
  const foot = el('div', {});
  if (existing && existing.id) {
    foot.appendChild(el('button', { class: 'btn sm danger', type: 'button', text: 'Изтрий', style: 'margin-right:auto', onClick: async () => {
      if (!(await confirmDialog('Да изтрия ли листата „' + (l.name || '') + '"?', { okText: 'Изтрий', danger: true }))) return;
      try { await api('/price-lists/' + existing.id, { method: 'DELETE' }); toast('Листата е изтрита.'); closeModal(); after(); }
      catch (e) { toast(e.message, true); }
    } }));
  }
  foot.appendChild(el('button', { class: 'btn ghost', type: 'button', text: 'Отказ', onClick: closeModal }));
  foot.appendChild(el('button', { class: 'btn', type: 'button', text: 'Запази', onClick: save, style: 'margin-left:8px' }));
  openModal({ title: (existing && existing.id) ? 'Ценова листа „' + (l.name || '') + '"' : 'Нова ценова листа', body, foot, wide: true });
}
